import { Alert, Badge, Button, Dialog, LinkButton, problemText, Section, TextField, type Tone } from "@emdur/ui";
import { type FormEvent, useEffect, useState } from "react";
import { call } from "../lib/api";
import { canDeactivate, canRestore, kindLabel, type Org, type OrgState, stateLabel } from "../lib/orgs";
import { DeactivateOrganization, RestoreOrganization } from "./DeactivateOrganization";

const TONE: Record<OrgState, Tone> = { pending_admin: "warning", active: "positive", inactive: "neutral" };
type Page = { items: Org[]; next_cursor?: string };

/** listOrganizations (W1.A5): every organisation, for a platform administrator; nothing for anyone else (403).
 * A pending organisation can be given its owner's invitation or deactivated from here. */
export function AllOrganizations({
  run,
  refresh,
}: {
  run: (work: Promise<unknown>) => Promise<void>;
  refresh: number;
}) {
  const [orgs, setOrgs] = useState<Org[] | null>(null);
  const [cursor, setCursor] = useState<string | undefined>();
  const [inviting, setInviting] = useState<Org | null>(null);
  const [closing, setClosing] = useState<Org | null>(null);
  const [restoring, setRestoring] = useState<Org | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function load(after?: string) {
    const r = await call("GET", `/v1/organizations${after ? `?cursor=${encodeURIComponent(after)}` : ""}`);
    // 403: not a platform administrator (members use their own list); 404/405: a Foundation without the operation yet.
    if ([403, 404, 405].includes(r.status)) return setOrgs(null);
    if (!r.ok) throw new Error(await problemText(r));
    const page = (await r.json()) as Page;
    setOrgs((before) => (after ? [...(before ?? []), ...page.items] : page.items));
    setCursor(page.next_cursor);
  }

  useEffect(() => void run(load()), [refresh]);

  const replace = (done: Org) => setOrgs((list) => list?.map((o) => (o.id === done.id ? { ...o, ...done } : o)) ?? null);

  if (!orgs) return null;
  return (
    <Section title="All organisations" description="Every organisation on the platform, including those waiting for their owner.">
      <ul aria-label="All organisations" className="flex flex-col gap-3">
        {orgs.map((o) => (
          <li key={o.id} className="flex flex-wrap items-center gap-2">
            <LinkButton variant="quiet" href={`/orgs/${o.id}`}>
              {o.display_name}
            </LinkButton>
            <span className="text-sm text-muted">{kindLabel(o.kind)}</span>
            <Badge tone={TONE[o.state] ?? "neutral"}>{stateLabel(o.state)}</Badge>
            {o.state === "pending_admin" && (
              <>
                <Button variant="quiet" onPress={() => setInviting(o)}>
                  Invite the owner
                </Button>
                {canDeactivate(o) && (
                  <Button variant="quiet" onPress={() => setClosing(o)}>
                    Deactivate
                  </Button>
                )}
              </>
            )}
            {canRestore(o) && (
              <Button variant="quiet" onPress={() => setRestoring(o)}>
                Restore
              </Button>
            )}
          </li>
        ))}
      </ul>
      {cursor && (
        <div className="mt-3">
          <Button onPress={() => void run(load(cursor))}>Show more</Button>
        </div>
      )}
      {message && (
        <div className="mt-3">
          <Alert tone="positive">{message}</Alert>
        </div>
      )}
      {inviting && (
        <InviteOwner
          org={inviting}
          run={run}
          onClose={() => setInviting(null)}
          onSent={(email) => {
            setInviting(null);
            setMessage(`Invitation sent to ${email}. ${inviting.display_name} becomes active when they accept.`);
          }}
        />
      )}
      {restoring && (
        <Dialog title={`Restore ${restoring.display_name}`} role="alertdialog" isOpen onOpenChange={(open) => !open && setRestoring(null)}>
          <div className="flex flex-col gap-4">
            <p>It returns to how it was: active if it has members (they sign in again), otherwise waiting for its owner.</p>
            <RestoreOrganization
              org={restoring}
              run={run}
              onDone={(done) => {
                replace(done);
                setRestoring(null);
                setMessage(`${done.display_name} is restored (${stateLabel(done.state)}).`);
              }}
            />
            <div>
              <Button onPress={() => setRestoring(null)}>Cancel</Button>
            </div>
          </div>
        </Dialog>
      )}
      {closing && (
        <Dialog title={`Deactivate ${closing.display_name}`} role="alertdialog" isOpen onOpenChange={(open) => !open && setClosing(null)}>
          <div className="flex flex-col gap-4">
            <p>No one can accept its invitations or use it afterwards. Its records are kept.</p>
            <DeactivateOrganization
              org={closing}
              run={run}
              onDone={(done) => {
                replace(done);
                setClosing(null);
                setMessage(`${done.display_name} is deactivated.`);
              }}
            />
            <div>
              <Button onPress={() => setClosing(null)}>Cancel</Button>
            </div>
          </div>
        </Dialog>
      )}
    </Section>
  );
}

function InviteOwner({
  org,
  run,
  onClose,
  onSent,
}: {
  org: Org;
  run: (work: Promise<unknown>) => Promise<void>;
  onClose: () => void;
  onSent: (email: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  function send(event: FormEvent) {
    event.preventDefault();
    void run(
      (async () => {
        const r = await call("POST", `/v1/organizations/${org.id}/invitations`, { email, roles: ["admin"] });
        if (!r.ok) return setError(await problemText(r));
        onSent(email);
      })(),
    );
  }
  return (
    <Dialog title={`Invite the owner of ${org.display_name}`} isOpen onOpenChange={(open) => !open && onClose()}>
      <form onSubmit={send} className="flex flex-col gap-3">
        <TextField label="Owner's email" type="email" value={email} onChange={setEmail} isRequired />
        {error && <Alert tone="danger">{error}</Alert>}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="primary">
            Send invitation
          </Button>
          <Button onPress={onClose}>Cancel</Button>
        </div>
      </form>
    </Dialog>
  );
}
